// ============================================================================
// Hook per gestione classi (corsi/visite) — Supabase
// ============================================================================

import { useState, useEffect, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase";

// Mappa tipo classe → campo(i) in dipendenti_scheda + durata rinnovo in anni
const TIPO_FIELD_MAP = {
  visita: { fields: ["visita_medica"], anni: 1 },
  formazione: { fields: ["form_gen", "form_spec"], anni: 5 },
  antincendio: { fields: ["antincendio"], anni: 3 },
  primo_soccorso: { fields: ["primo_soccorso"], anni: 3 },
  piattaforma: { fields: ["piattaforma"], anni: 5 },
  rls: { fields: ["rls"], anni: 1 },
  lav_quota: { fields: ["lav_quota"], anni: 5 },
};

export { TIPO_FIELD_MAP };

function addYears(dateStr, years) {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().split("T")[0]; // YYYY-MM-DD
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export function useClassi() {
  const [classi, setClassi] = useState([]);
  const [classiDipendenti, setClassiDipendenti] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---------- Fetch iniziale + realtime ----------
  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }

    Promise.all([
      supabase.from("classi").select("*").order("data", { ascending: true }),
      supabase.from("classi_dipendenti").select("*"),
    ]).then(([resClassi, resDip]) => {
      if (resClassi.error) console.error("Errore caricamento classi:", resClassi.error);
      if (resDip.error) console.error("Errore caricamento classi_dipendenti:", resDip.error);
      if (resClassi.data) setClassi(resClassi.data);
      if (resDip.data) setClassiDipendenti(resDip.data);
      setLoading(false);
    });

    const ch = supabase
      .channel("classi_realtime")
      // classi
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "classi" }, (p) => {
        setClassi((prev) => prev.some((c) => c.id === p.new.id) ? prev : [...prev, p.new]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "classi" }, (p) => {
        setClassi((prev) => prev.map((c) => c.id === p.new.id ? p.new : c));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "classi" }, (p) => {
        setClassi((prev) => prev.filter((c) => c.id !== p.old.id));
      })
      // classi_dipendenti
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "classi_dipendenti" }, (p) => {
        setClassiDipendenti((prev) => prev.some((d) => d.id === p.new.id) ? prev : [...prev, p.new]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "classi_dipendenti" }, (p) => {
        setClassiDipendenti((prev) => prev.map((d) => d.id === p.new.id ? p.new : d));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "classi_dipendenti" }, (p) => {
        setClassiDipendenti((prev) => prev.filter((d) => d.id !== p.old.id));
      })
      .subscribe();

    return () => supabase.removeChannel(ch);
  }, []);

  // ---------- Helper: dipendenti per classe ----------
  const getDipendentiClasse = useCallback(
    (classeId) => classiDipendenti.filter((d) => d.classe_id === classeId),
    [classiDipendenti]
  );

  // ---------- saveClasse (create or update) ----------
  const saveClasse = useCallback(async (data) => {
    if (!isSupabaseConfigured) return { success: false, error: "Supabase non configurato" };
    if (data.id) {
      // UPDATE
      const { id, ...rest } = data;
      const { data: updated, error } = await supabase.from("classi").update(rest).eq("id", id).select();
      if (error) { console.error("Errore aggiornamento classe:", error); return { success: false, error: error.message }; }
      setClassi((prev) => prev.map((c) => c.id === id ? updated[0] : c));
      return { success: true, data: updated[0] };
    } else {
      // INSERT
      const { data: inserted, error } = await supabase.from("classi").insert([data]).select();
      if (error) { console.error("Errore creazione classe:", error); return { success: false, error: error.message }; }
      setClassi((prev) => prev.some((c) => c.id === inserted[0].id) ? prev : [...prev, inserted[0]]);
      return { success: true, data: inserted[0] };
    }
  }, []);

  // ---------- deleteClasse ----------
  const deleteClasse = useCallback(async (id) => {
    setClassi((prev) => prev.filter((c) => c.id !== id));
    setClassiDipendenti((prev) => prev.filter((d) => d.classe_id !== id));
    if (!isSupabaseConfigured) return;
    await supabase.from("classi_dipendenti").delete().eq("classe_id", id);
    await supabase.from("classi").delete().eq("id", id);
  }, []);

  // ---------- addDipendenteClasse ----------
  const addDipendenteClasse = useCallback(async (classeId, dipId, dipNome) => {
    if (!isSupabaseConfigured) return { success: false };
    const row = { classe_id: classeId, dipendente_id: dipId, dipendente_nome: dipNome, presente: false };
    const { data, error } = await supabase.from("classi_dipendenti").insert([row]).select();
    if (error) { console.error("Errore aggiunta dip a classe:", error); return { success: false, error: error.message }; }
    setClassiDipendenti((prev) => prev.some((d) => d.id === data[0].id) ? prev : [...prev, data[0]]);
    return { success: true };
  }, []);

  // ---------- removeDipendenteClasse ----------
  const removeDipendenteClasse = useCallback(async (classeId, dipId) => {
    setClassiDipendenti((prev) => prev.filter((d) => !(d.classe_id === classeId && d.dipendente_id === dipId)));
    if (!isSupabaseConfigured) return;
    await supabase.from("classi_dipendenti").delete().eq("classe_id", classeId).eq("dipendente_id", dipId);
  }, []);

  // ---------- marcaCompletata ----------
  const marcaCompletata = useCallback(async (classeId, presentiIds, { visiteProgrammate, saveVisiteProgrammate, formazioneProgrammate, saveFormazioneProgrammate } = {}) => {
    if (!isSupabaseConfigured) return { success: false };

    const classe = classi.find((c) => c.id === classeId);
    if (!classe) return { success: false, error: "Classe non trovata" };

    const mapping = TIPO_FIELD_MAP[classe.tipo];
    if (!mapping) return { success: false, error: "Tipo classe non riconosciuto" };

    const oggi = todayISO();
    const nuovaScadenza = addYears(oggi, mapping.anni);

    // 1. Aggiorna presenze in classi_dipendenti
    const dipClasse = classiDipendenti.filter((d) => d.classe_id === classeId);
    for (const d of dipClasse) {
      const presente = presentiIds.includes(d.dipendente_id);
      await supabase.from("classi_dipendenti").update({ presente }).eq("id", d.id);
    }
    setClassiDipendenti((prev) =>
      prev.map((d) =>
        d.classe_id === classeId ? { ...d, presente: presentiIds.includes(d.dipendente_id) } : d
      )
    );

    // 2. Aggiorna dipendenti_scheda per i presenti
    for (const dipId of presentiIds) {
      const updates = {};
      for (const field of mapping.fields) {
        updates[field] = nuovaScadenza;
      }
      const { error } = await supabase.from("dipendenti_scheda").upsert([{ id: dipId, ...updates }], { onConflict: "id" });
      if (error) console.error(`Errore aggiornamento scheda dip ${dipId}:`, error);
    }

    // 3. Rimuovi da visiteProgrammate o formazioneProgrammate i presenti
    if (classe.tipo === "visita" && visiteProgrammate && saveVisiteProgrammate) {
      const next = { ...visiteProgrammate };
      for (const dipId of presentiIds) delete next[dipId];
      saveVisiteProgrammate(next);
    } else if (classe.tipo !== "visita" && formazioneProgrammate && saveFormazioneProgrammate) {
      const next = { ...formazioneProgrammate };
      for (const dipId of presentiIds) delete next[dipId];
      saveFormazioneProgrammate(next);
    }

    // 4. Segna classe completata
    const { error } = await supabase.from("classi").update({ completata: true }).eq("id", classeId);
    if (error) { console.error("Errore completamento classe:", error); return { success: false, error: error.message }; }
    setClassi((prev) => prev.map((c) => c.id === classeId ? { ...c, completata: true } : c));

    return { success: true, nuovaScadenza };
  }, [classi, classiDipendenti]);

  return {
    classi,
    classiDipendenti,
    loading,
    getDipendentiClasse,
    saveClasse,
    deleteClasse,
    addDipendenteClasse,
    removeDipendenteClasse,
    marcaCompletata,
  };
}
